import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Image, Card, Button, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BlogView.css';

const BlogView = () => {
    const { id } = useParams();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const isAuthenticated = localStorage.getItem('token') !== null;
    const navigate = useNavigate();

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await axios.get(`https://api.admin-panel.nhcholding.com/api/news/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setNews(response.data);
            } catch (error) {
                console.error('Error fetching news:', error);
                toast.error('Failed to load news details.');
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, [id]);

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    if (loading) {
        return (
            <Container fluid className="news-view-container">
                <div className="loading-container">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3">Loading news details...</p>
                </div>
            </Container>
        );
    }

    if (!news) {
        return (
            <Container fluid className="news-view-container">
                <div className="error-container">
                    <h4>News not found</h4>
                    <Button variant="primary" onClick={handleGoBack} className="mt-3">
                        Go Back
                    </Button>
                </div>
            </Container>
        );
    }

    const handleGoBack = () => {
        navigate(-1); // Goes back to the previous page in history
    };

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved & Published', variant: 'success' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            manager_approved: { text: 'Approved by Manager', variant: 'info' },
            pending: { text: 'In Review', variant: 'secondary' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'dark' };
        return <Badge bg={variant}>{text}</Badge>;
    };

    return (
        <div className="news-view-container">
            <ToastContainer />
            
            {/* Header Section */}
            <div className="news-header">
                <Container fluid>
                    <div className="header-controls">
                        <Button 
                            variant="outline-primary" 
                            onClick={handleGoBack}
                            className="back-button"
                        >
                            <i className="bi bi-arrow-left me-2"></i>
                            Back to News Management
                        </Button>
                        <div className="header-actions">
                            <Button 
                                variant="warning" 
                                size="sm"
                                onClick={() => navigate(`/cms/blog-management/edit/${id}`)}
                                className="me-2"
                            >
                                <i className="bi bi-pencil-square me-1"></i>
                                Edit
                            </Button>
                            {getStatusBadge(news.status)}
                        </div>
                    </div>
                </Container>
            </div>

            {/* Main Article Section */}
            <Container fluid className="article-container">
                <Row className="justify-content-center">
                    <Col lg={10} xl={8}>
                        <article className="news-article">
                            
                            {/* Article Header */}
                            <header className="article-header">
                                <div className="title-section">
                                    <h1 className="title-english">{news.title_en}</h1>
                                    <h2 className="title-arabic" dir="rtl">{news.title_ar}</h2>
                                </div>
                                
                                <div className="article-meta">
                                    <div className="meta-item">
                                        <i className="bi bi-calendar3"></i>
                                        <span>{new Date(news.created_at).toLocaleDateString('en-GB', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}</span>
                                    </div>
                                    <div className="meta-item">
                                        <i className="bi bi-person-circle"></i>
                                        <span>{news.creator?.name || 'Unknown Author'}</span>
                                    </div>
                                    <div className="meta-item">
                                        <i className="bi bi-eye"></i>
                                        <Badge bg={news.is_active ? 'success' : 'secondary'}>
                                            {news.is_active ? 'Published' : 'Draft'}
                                        </Badge>
                                    </div>
                                </div>
                            </header>

                            {/* Featured Image */}
                            {news.featured_image && (
                                <div className="featured-image-container">
                                    <Image
                                        src={`https://api.admin-panel.nhcholding.com/storage/${news.featured_image}`}
                                        alt="Featured Image"
                                        className="featured-image"
                                        fluid
                                    />
                                </div>
                            )}

                            {/* Article Content */}
                            <div className="article-content">
                                <div className="content-section">
                                    <h3 className="content-title">
                                        <i className="bi bi-translate me-2"></i>
                                        English Content
                                    </h3>
                                    <div 
                                        className="content-body english-content" 
                                        dangerouslySetInnerHTML={{ __html: news.content_en }} 
                                    />
                                </div>

                                <div className="content-section">
                                    <h3 className="content-title">
                                        <i className="bi bi-translate me-2"></i>
                                        Arabic Content
                                    </h3>
                                    <div 
                                        className="content-body arabic-content" 
                                        dir="rtl"
                                        dangerouslySetInnerHTML={{ __html: news.content_ar }} 
                                    />
                                </div>
                            </div>

                            {/* Article Footer with Admin Details */}
                            <footer className="article-footer">
                                <div className="admin-details">
                                    <h4>
                                        <i className="bi bi-gear me-2"></i>
                                        Administrative Details
                                    </h4>
                                    
                                    <Row>
                                        <Col md={6}>
                                            <div className="detail-item">
                                                <strong>News ID:</strong> {news.id}
                                            </div>
                                            <div className="detail-item">
                                                <strong>Created Date:</strong> {new Date(news.created_at).toLocaleString()}
                                            </div>
                                            {news.published_at && (
                                                <div className="detail-item">
                                                    <strong>Published Date:</strong> {new Date(news.published_at).toLocaleString()}
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={6}>
                                            <div className="detail-item">
                                                <strong>Status:</strong> {getStatusBadge(news.status)}
                                            </div>
                                            <div className="detail-item">
                                                <strong>Active Status:</strong> 
                                                <Badge bg={news.is_active ? 'success' : 'danger'} className="ms-2">
                                                    {news.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                            <div className="detail-item">
                                                <strong>Created By:</strong> {news.creator?.name || 'Unknown'}
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Comments and Feedback */}
                                    {(news.manager_comment || news.it_comment || news.rejection_reason) && (
                                        <div className="feedback-section">
                                            <h5>
                                                <i className="bi bi-chat-dots me-2"></i>
                                                Comments & Feedback
                                            </h5>
                                            
                                            {news.manager_comment && (
                                                <div className="comment-item manager-comment">
                                                    <strong>Manager Comment:</strong>
                                                    <p>{news.manager_comment}</p>
                                                </div>
                                            )}
                                            
                                            {news.it_comment && (
                                                <div className="comment-item it-comment">
                                                    <strong>IT Comment:</strong>
                                                    <p>{news.it_comment}</p>
                                                </div>
                                            )}
                                            
                                            {news.rejection_reason && (
                                                <div className="comment-item rejection-reason">
                                                    <strong>Rejection Reason:</strong>
                                                    <p className="text-danger">{news.rejection_reason}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </footer>
                        </article>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default BlogView; 